import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import type { AdminApi } from "@/api";
import { Banner, Button, Card, CardHead, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { shownPageId } from "@/components/test-result/shownPage";
import type { BatchCostOverview, BatchPageMode, BatchPageRow, TeSpec } from "@/types";
import { compactTime } from "@/utils/format";
import { costTitle, formatCost } from "./cost";
import { CostOverview } from "./CostOverview";
import { specKey } from "./teSpec";

/** The page's TEs as letters, oldest first — A is its baseline. */
function letters(row: BatchPageRow): string[] {
  const seen: string[] = [];
  for (const spec of row.te_specs) {
    const key = specKey(spec);
    if (!seen.includes(key)) seen.push(key);
  }
  return seen.map((_, index) => `TE-${String.fromCharCode(65 + index)}`);
}

/** The letter this page gave a TE — the running one, for "실행 중 · TE-B". */
function letterOf(row: BatchPageRow, spec: TeSpec | undefined): string | null {
  if (!spec) return null;
  const keys: string[] = [];
  for (const s of row.te_specs) {
    const key = specKey(s);
    if (!keys.includes(key)) keys.push(key);
  }
  const index = keys.indexOf(specKey(spec));
  return index < 0 ? null : `TE-${String.fromCharCode(65 + index)}`;
}

/**
 * Batch Test — the list of pages (fe/TEST_CASES_BATCH.md §1).
 *
 * A page is a fixed TC set and the runs made over it. Creating one opens it
 * straight away with **replace** (§1.3): pushing would leave a back entry to a
 * state that no longer exists, and forward would create the same page again.
 */
export function BatchTestListPage({ api, reloadKey = 0 }: { api: AdminApi; reloadKey?: number }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BatchPageRow[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState<BatchPageMode | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [costs, setCosts] = useState<BatchCostOverview | null>(null);
  const [costError, setCostError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.listBatchPages();
      setRows(res.items);
      setNextCursor(res.next_cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows([]);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  // Apart from the list: a cost read that fails leaves the pages usable.
  useEffect(() => {
    setCostError("");
    api.getBatchCosts().then(setCosts, (err) => setCostError(err instanceof Error ? err.message : String(err)));
  }, [api, reloadKey]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await api.listBatchPages({ cursor: nextCursor });
      setRows((prev) => [...(prev ?? []), ...res.items]);
      setNextCursor(res.next_cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const create = async (mode: BatchPageMode) => {
    setCreating(mode);
    setError("");
    try {
      const page = await api.createBatchPage({ mode });
      navigate({
        to: "/test-cases/batch/$pageId",
        params: { pageId: page.page_id },
        replace: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCreating(null);
    }
  };

  const open = (row: BatchPageRow) =>
    navigate({ to: "/test-cases/batch/$pageId", params: { pageId: row.page_id } });

  const shownId = shownPageId(rows);

  return (
    <div className="grid gap-4">
      {error && <Banner tone="error">{error}</Banner>}
      <CostOverview data={costs} error={costError} />
      <Card className="overflow-hidden">
        <CardHead title="Batch Test 페이지" hint="TC 를 고정하고 · 그 안에서 TE 를 하나씩 골라 실행한다">
          <Button variant="outline" size="sm" disabled={creating !== null} onClick={() => void create("single")}>
            {creating === "single" && <Loader2 className="animate-spin" />}+ 단일 TC
          </Button>
          <Button size="sm" disabled={creating !== null} onClick={() => void create("multi")}>
            {creating === "multi" && <Loader2 className="animate-spin" />}+ 다중 TC
          </Button>
        </CardHead>

        {rows === null ? (
          <div className="grid gap-2 p-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-11" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="grid justify-items-center gap-2 px-4 py-12 text-center">
            <p className="text-sm">아직 만든 Batch Test 가 없습니다.</p>
            <p className="text-xs text-muted-foreground">
              + 로 페이지를 만들어 TC 묶음을 고정한 뒤, 그 안에서 TE 를 하나씩 골라 실행합니다.
            </p>
            <Button size="sm" className="mt-2" disabled={creating !== null} onClick={() => void create("multi")}>
              + 다중 TC
            </Button>
          </div>
        ) : (
          <div role="list">
            <div className="hidden grid-cols-[72px_minmax(0,1fr)_110px_minmax(0,160px)_130px_90px_110px] gap-3 border-b border-border px-4 py-2 text-[11px] text-muted-foreground md:grid">
              <span>ID</span>
              <span>이름</span>
              <span>TC</span>
              <span>실행한 TE</span>
              <span>마지막 실행</span>
              <span className="text-right">비용</span>
              <span />
            </div>
            {rows.map((row) => {
              const live = row.last_run?.status === "running";
              const liveLetter = live ? letterOf(row, row.last_run?.te_spec) : null;
              const shown = row.page_id === shownId;
              const tes = letters(row);
              return (
                <button
                  key={row.page_id}
                  type="button"
                  role="listitem"
                  onClick={() => open(row)}
                  className="grid w-full cursor-pointer grid-cols-[72px_minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-accent-hover md:grid-cols-[72px_minmax(0,1fr)_110px_minmax(0,160px)_130px_90px_110px]"
                >
                  <span className="text-xs font-medium tabular-nums">{row.label}</span>
                  <span className="grid min-w-0 gap-0.5">
                    <span className="truncate text-[13px] font-medium">{row.name}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      생성 {compactTime(row.created_at)} · {row.created_by_email ?? "—"}
                    </span>
                  </span>
                  <span className="hidden text-xs text-muted-foreground md:block">
                    {row.mode === "single" ? "단일" : "다중"} ·{" "}
                    {row.locked ? `${row.test_case_ids.length} TC` : "선택 중"}
                  </span>
                  <span className="hidden flex-wrap gap-1 md:flex">
                    {tes.length === 0 ? (
                      <span className="text-[11px] text-muted-foreground">실행 전</span>
                    ) : (
                      tes.map((te) => (
                        <span key={te} className="rounded-full border border-border px-1.5 text-[10px] text-muted-foreground">
                          {te}
                        </span>
                      ))
                    )}
                  </span>
                  <span className="hidden text-[11px] text-muted-foreground tabular-nums md:block">
                    {row.last_run ? compactTime(row.last_run.created_at) : "—"}
                  </span>
                  <span
                    className="hidden text-right text-[11px] text-muted-foreground tabular-nums md:block"
                    title={costTitle(row.cost)}
                  >
                    {formatCost(row.cost)}
                  </span>
                  {/* Artifact: what the page is doing, or that it is the one
                      the Matrix shows, before the plain "open". */}
                  <span
                    className={cn(
                      "justify-self-end text-[11px]",
                      live || shown ? "font-medium text-primary" : "text-muted-foreground",
                    )}
                  >
                    {live ? `실행 중${liveLetter ? ` · ${liveLetter}` : ""}` : shown ? "Matrix 표시 중" : "열기 ›"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Card>
      {nextCursor && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore && <Loader2 className="animate-spin" />}더 보기
          </Button>
        </div>
      )}
    </div>
  );
}
